import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { PetAvatar } from '@/components/PetAvatar'
import { usePetName } from '@/hooks/usePetName'
import { usePetState } from '@/hooks/usePetState'
import { FutureYouChat } from '@/components/FutureYouChat'
import { getSpeciesName, speciesDescription, OEN_AXIS_LABEL, type Species } from '@/lib/speciesNames'
import { ManualSection } from '@/pages/Manual'
import { MirrorSection } from '@/pages/Me'
import { StorySection } from '@/pages/Story'

interface OceanEstimate {
  id: number
  estimated_at: string
  openness: number
  conscientiousness: number
  extraversion: number
  agreeableness: number
  neuroticism: number
  evidence: { axis: string; quote: string; diary_id?: number }[] | null
  derived_oen: string | null
  derived_ca: string | null
  diary_count: number
}

interface SmoothedOceanView {
  smoothed_at: string | null
  smoothed_openness: number | null
  smoothed_conscientiousness: number | null
  smoothed_extraversion: number | null
  smoothed_agreeableness: number | null
  smoothed_neuroticism: number | null
  smoothed_oen: string | null
  derived_ca: string | null
  latest: OceanEstimate | null
}

interface Session {
  id: string
  title: string | null
  summary: string | null
  message_count: number
  started_at: string
  last_active_at: string
  ended_at: string | null
}

interface Interaction {
  id: number
  user_message: string | null
  assistant_message: string | null
  created_at: string
}

const STAGE_LABEL: Record<string, string> = {
  egg: '卵',
  hatch: 'ヒナ',
  juvenile: '子',
  mature: '成体',
  sage: '老成',
}

type PartnerTab = 'analysis' | 'talk'

const PARTNER_TABS: Array<{ key: PartnerTab; label: string; description: string }> = [
  { key: 'analysis', label: '自己分析', description: 'AIが日記から読み取った、今のあなた。性格・くり返すパターン・気づいていない死角・今の局面まで。' },
  { key: 'talk', label: '対話', description: 'AIと話しながら自分を掘る場所。過去の会話もここから振り返れます。' },
]

/**
 * 旧タブ（相棒/取扱説明書/鏡/物語）からの後方互換マッピング。
 * mirror / manual / story / insights / weekly / analysis → 自己分析
 * companion / talk → 対話
 */
function normalizeTab(value: string | null): PartnerTab {
  if (value === 'talk' || value === 'companion') return 'talk'
  return 'analysis'
}

/**
 * アバター + 名前 + 状態のヘッダー。自己分析・対話の両タブで共通して上部に置く。
 * caption だけ文脈で変える（自己分析=性格の表現 / 対話=話す相手）。
 */
function AvatarHeader({ petName, petState, caption }: {
  petName: string
  petState: ReturnType<typeof usePetState>['state']
  caption: string
}) {
  return (
    <header style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
      <PetAvatar size={88} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>{caption}</div>
        <div style={{ fontSize: 18, fontWeight: 500 }}>{petName}</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
          {petState ? (
            <>
              {STAGE_LABEL[petState.stage] || petState.stage}
              {petState.inCocoon && ' · 変身中'}
              {petState.coverage > 0 && !petState.inCocoon && ` · 理解度 ${petState.coverage}%`}
            </>
          ) : (
            '読み込み中…'
          )}
        </div>
      </div>
    </header>
  )
}

/** /partner — 相棒のホーム。アバター + 現在の対話 + 過去履歴を 1 ページに集約。 */
export default function Partner() {
  const { name: petName } = usePetName()
  const { state: petState } = usePetState()
  const [searchParams, setSearchParams] = useSearchParams()
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState<string | null>(null)
  const [interactions, setInteractions] = useState<Interaction[]>([])
  const [loadingInteractions, setLoadingInteractions] = useState(false)
  const [keyword, setKeyword] = useState('')
  const activeTab = normalizeTab(searchParams.get('tab'))

  const setTab = (tab: PartnerTab) => {
    setSearchParams(tab === 'analysis' ? {} : { tab })
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [activeTab])

  const fetchSessions = useCallback(async () => {
    const { data } = await supabase
      .from('chat_sessions')
      .select('id, title, summary, message_count, started_at, last_active_at, ended_at')
      .order('started_at', { ascending: false })
      .limit(200)
    setSessions((data || []) as Session[])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  const open = async (id: string) => {
    if (openId === id) {
      setOpenId(null)
      setInteractions([])
      return
    }
    setOpenId(id)
    setLoadingInteractions(true)
    setInteractions([])
    const { data } = await supabase
      .from('chat_interactions')
      .select('id, user_message, assistant_message, created_at')
      .eq('session_id', id)
      .order('created_at', { ascending: true })
    setInteractions((data || []) as Interaction[])
    setLoadingInteractions(false)
  }

  // 検索フィルタ (title / summary に keyword が含まれるもの)
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase()
    if (!kw) return sessions
    return sessions.filter((s) => {
      return (s.title || '').toLowerCase().includes(kw) || (s.summary || '').toLowerCase().includes(kw)
    })
  }, [sessions, keyword])

  return (
    <div style={{ padding: '24px 16px', maxWidth: 720, margin: '0 auto' }}>
      {/* ページタイトル: 自己理解の入り口だと一目でわかるように
          (旧 Partner ページから「自分について」にリネーム) */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>あなたについて</h1>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
          AIがあなたについて分析したことと、AIとの対話。
        </div>
      </div>

      <div
        role="tablist"
        aria-label="あなたについて"
        style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          borderBottom: '1px solid var(--border)',
          marginBottom: 20,
        }}
      >
        {PARTNER_TABS.map((tab) => {
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              title={tab.description}
              onClick={() => setTab(tab.key)}
              style={{
                minHeight: 38,
                padding: '8px 12px',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--accent)' : 'var(--text3)',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                cursor: 'pointer',
                marginBottom: -1,
                fontFamily: 'var(--font)',
              }}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* アクティブなタブの説明を常時表示 (メタファーに頼らず、何が見えるかを明示) */}
      <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginTop: -8, marginBottom: 20 }}>
        {PARTNER_TABS.find((t) => t.key === activeTab)?.description}
      </div>

      {activeTab === 'analysis' && (
        <>
          <AvatarHeader petName={petName} petState={petState} caption="あなたの性格から生まれた相棒" />

          {/* 性格 (OCEAN 推定) — 相棒の姿の根拠 */}
          {petState && <PersonalityExplanation species={petState.species} />}

          {/* 今の物語: 局面(arc) / テーマ / 感情のパターン */}
          <StorySection />

          {/* 日記から見えたこと: パターン・変化・盲点・年表 (self_doc) */}
          <div style={{ marginTop: 8 }}>
            <MirrorSection />
          </div>

          {/* あなたの取扱説明書 (編集は控えめ) */}
          <div style={{ marginTop: 24 }}>
            <ManualSection embedded />
          </div>
        </>
      )}

      {activeTab === 'talk' && (
        <>
          <AvatarHeader petName={petName} petState={petState} caption="あなたの相棒" />

          {/* 現在の対話 (FutureYouChat 流用、24h 以内のセッションを継続) */}
          <FutureYouChat entryPoint="partner_page" />

          {/* 過去の会話履歴 */}
          <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <h2 style={{ fontSize: 14, fontWeight: 500, margin: 0, color: 'var(--text)' }}>過去の会話</h2>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{filtered.length}件</span>
        </div>
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="キーワードで絞り込む（タイトル・要約から検索）"
          className="input"
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '6px 10px', marginBottom: 12 }}
        />

        {loading && <div style={{ fontSize: 13, color: 'var(--text3)' }}>読み込み中…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)', padding: 12 }}>
            {keyword ? '一致する会話がありません' : 'まだ会話がありません'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filtered.map((s) => {
            const date = (s.started_at || '').slice(0, 10)
            const isActive = !s.ended_at
            const isOpen = openId === s.id
            return (
              <Card key={s.id} style={{ padding: 10 }}>
                <button
                  type="button"
                  onClick={() => open(s.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    padding: 0,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 11, color: 'var(--text2)' }}>{date}</span>
                    {isActive && (
                      <span style={{ fontSize: 9, color: 'var(--accent, #5046E5)', padding: '0 5px', border: '1px solid var(--accent, #5046E5)', borderRadius: 3 }}>
                        active
                      </span>
                    )}
                    <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 'auto' }}>{s.message_count}件</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>
                    {s.summary || s.title || '(無題)'}
                  </div>
                </button>

                {isOpen && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                    {loadingInteractions && <div style={{ fontSize: 11, color: 'var(--text3)' }}>読み込み中…</div>}
                    {!loadingInteractions && interactions.map((it) => (
                      <div key={it.id} style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.6 }}>
                        {it.user_message && (
                          <div style={{ background: 'var(--surface2)', padding: 7, borderRadius: 5, marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                            {it.user_message}
                          </div>
                        )}
                        {it.assistant_message && (
                          <div style={{ background: 'var(--surface)', padding: 7, borderRadius: 5, border: '1px solid var(--border)', whiteSpace: 'pre-wrap' }}>
                            {it.assistant_message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      </div>
        </>
      )}
    </div>
  )
}

/**
 * PersonalityExplanation — 自分がどういう性格・考え方として推定されているか、
 * その根拠の日記引用と OCEAN スコアを開示するセクション。
 *
 * 設計意図: 「なぜこの判断になったのか」「どんな性格・考え方か」を、
 * 推定の透明性として示す。ユーザーが納得 or 違和感を持てる情報量を担保する。
 */
function PersonalityExplanation({ species }: { species: Species }) {
  const [estimate, setEstimate] = useState<SmoothedOceanView | null>(null)
  const [loading, setLoading] = useState(true)
  const [showEvidence, setShowEvidence] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      const { data: petState } = await supabase
        .from('pet_state')
        .select('smoothed_at, smoothed_openness, smoothed_conscientiousness, smoothed_extraversion, smoothed_agreeableness, smoothed_neuroticism, smoothed_oen, derived_ca')
        .limit(1)
        .maybeSingle()

      const { data: latest } = await supabase
        .from('ocean_estimates')
        .select('id, estimated_at, openness, conscientiousness, extraversion, agreeableness, neuroticism, evidence, derived_oen, derived_ca, diary_count')
        .order('estimated_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (alive) {
        const ps = petState as Omit<SmoothedOceanView, 'latest'> | null
        const raw = latest as OceanEstimate | null
        setEstimate(ps ? { ...ps, latest: raw } : null)
        setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  const speciesName = getSpeciesName(species)
  const description = speciesDescription[species]

  /**
   * 5 軸の H/M/L 判定は推定アルゴリズム側の `derived_oen` (3 文字: O,E,N) と
   * `derived_ca` (2 文字: C,A) を真とする。値だけから閾値判定すると、
   * アルゴリズム本体の判定と乖離するため。
   */
  function levelFromDerived(axis: 'O'|'C'|'E'|'A'|'N'): 'H' | 'M' | 'L' | null {
    if (!estimate) return null
    const oen = estimate.smoothed_oen
    const ca = estimate.derived_ca
    const ch = (() => {
      if (axis === 'O' && oen) return oen[0]
      if (axis === 'E' && oen) return oen[1]
      if (axis === 'N' && oen) return oen[2]
      if (axis === 'C' && ca) return ca[0]
      if (axis === 'A' && ca) return ca[1]
      return null
    })()
    if (ch === 'H' || ch === 'M' || ch === 'L') return ch
    return null
  }

  return (
    <section style={{ marginTop: 20, marginBottom: 24 }}>
      <h2 style={{ fontSize: 14, fontWeight: 500, margin: '0 0 8px 0' }}>あなたの相棒について</h2>
      <Card style={{ padding: 14 }}>
        {/* 種族の意味 */}
        <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, marginBottom: 6 }}>
          <strong>{speciesName}型</strong> — {description}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 10 }}>
          相棒の姿は、最近の日記から推定された性格 (Big Five 5 軸) に基づいて選ばれます。
          推定が更新されると、姿や色合い (副軸) も少しずつ変わります。
        </div>

        {loading && <div style={{ fontSize: 11, color: 'var(--text3)' }}>推定根拠を読み込んでいます…</div>}

        {!loading && (!estimate || estimate.smoothed_openness == null) && (
          <div style={{ fontSize: 11, color: 'var(--text3)', padding: 8, background: 'var(--surface2)', borderRadius: 4 }}>
            まだ OCEAN 推定が実行されていません。Today で「推定する」ボタンを押すと、最近の日記から性格を推定します。
          </div>
        )}

        {!loading && estimate && estimate.smoothed_openness != null && (
          <>
            {/* OCEAN 5 軸スペクトラム
                各軸を「低⇔高」の両極ラベル付きバーで表示し、自分の位置をマーカーで示す。
                右端に H/M/L バッジ (= 種族判定の元となった粒度) を出す。 */}
            <div style={{ marginTop: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500, marginBottom: 8 }}>
                推定された性格 (Big Five)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {(['O','C','E','A','N'] as const).map((axis) => {
                  const valueMap = {
                    O: estimate.smoothed_openness,
                    C: estimate.smoothed_conscientiousness,
                    E: estimate.smoothed_extraversion,
                    A: estimate.smoothed_agreeableness,
                    N: estimate.smoothed_neuroticism,
                  }
                  const value = valueMap[axis]
                  if (value == null) return null
                  const meta = OEN_AXIS_LABEL[axis]
                  const level = levelFromDerived(axis)
                  return (
                    <OceanSpectrum
                      key={axis}
                      axisLetter={axis}
                      axisName={meta.name}
                      lowLabel={meta.low}
                      highLabel={meta.high}
                      value={value}
                      level={level}
                    />
                  )
                })}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 12 }}>
                {estimate.smoothed_at ? new Date(estimate.smoothed_at).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'short' }) : '更新日時なし'} 時点 ·
                直近 {estimate.latest?.diary_count ?? '—'} 件の日記から週次推定 ·
                判定: OEN={estimate.smoothed_oen ?? '—'} / CA={estimate.derived_ca ?? '—'}
              </div>
            </div>

            {/* 推定の根拠 (日記からの引用) */}
            {estimate.latest?.evidence && estimate.latest.evidence.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                <button
                  type="button"
                  onClick={() => setShowEvidence((v) => !v)}
                  style={{
                    fontSize: 11,
                    color: 'var(--text2)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontFamily: 'inherit',
                  }}
                >
                  {showEvidence ? '判断の根拠を閉じる ▲' : `判断の根拠を見る ▼ (${estimate.latest.evidence.length}件)`}
                </button>
                {showEvidence && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {estimate.latest.evidence.map((ev, i) => (
                      <div key={i} style={{ padding: 8, fontSize: 11, background: 'var(--surface2)', borderRadius: 4, lineHeight: 1.5 }}>
                        <div style={{ color: 'var(--text3)', marginBottom: 2, fontFamily: 'var(--mono)' }}>{ev.axis}</div>
                        <div style={{ color: 'var(--text)' }}>「{ev.quote}」</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Card>
    </section>
  )
}

/**
 * OceanSpectrum — 1 軸ぶんのスペクトラムバー。
 *
 *   O 開放性                                                          [H]
 *   現実的・実直 ────────────────●──── 想像力豊か・好奇心旺盛   72
 *
 * 設計意図: 「片極のラベルだけ + 数値」では、低い側がどう解釈されるか
 * 分からず、自分が中庸寄りなのか極端なのかも直感では掴めない。
 * 両極ラベル + 0–100 の位置マーカーで一目で読めるようにする。
 *
 * value は 0–100 想定 (DB に格納されている値そのまま)。
 * level (H/M/L) はアルゴリズム側の判定 (`derived_oen` / `derived_ca`) を渡す。
 */
function OceanSpectrum(props: {
  axisLetter: 'O'|'C'|'E'|'A'|'N'
  axisName: string
  lowLabel: string
  highLabel: string
  value: number
  level: 'H' | 'M' | 'L' | null
}) {
  const { axisLetter, axisName, lowLabel, highLabel, value, level } = props
  // value は 0-100 を想定。万一 0-1 で来てもクランプしておく。
  const clamped = Math.max(0, Math.min(100, value > 1 ? value : value * 100))
  const levelColor = level === 'H' ? 'var(--accent)' : level === 'L' ? 'var(--text2)' : 'var(--text3)'
  const levelLabel = level === 'H' ? '高' : level === 'L' ? '低' : level === 'M' ? '中' : '—'

  return (
    <div>
      {/* 1段目: 軸名 + H/M/L バッジ */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4, fontSize: 11 }}>
        <span style={{ width: 14, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{axisLetter}</span>
        <span style={{ flex: 1, color: 'var(--text2)' }}>{axisName}</span>
        <span style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          color: levelColor,
          padding: '1px 6px',
          border: `1px solid ${levelColor}`,
          borderRadius: 3,
          minWidth: 28,
          textAlign: 'center',
        }}>
          {level ?? '—'} {levelLabel}
        </span>
      </div>
      {/* 2段目: 低ラベル ─ バー(マーカー) ─ 高ラベル + 数値 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: 'var(--text3)' }}>
        <span style={{ minWidth: 110, textAlign: 'right', lineHeight: 1.3 }}>{lowLabel}</span>
        <div style={{
          flex: 1,
          position: 'relative',
          height: 6,
          background: 'var(--surface2)',
          borderRadius: 3,
        }}>
          {/* 中央 (50%) のメモリ */}
          <div style={{
            position: 'absolute',
            left: '50%',
            top: -2,
            bottom: -2,
            width: 1,
            background: 'var(--border)',
          }} />
          {/* 自分の位置マーカー */}
          <div style={{
            position: 'absolute',
            left: `${clamped}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: levelColor,
            border: '2px solid var(--surface)',
            boxShadow: '0 0 0 1px var(--border)',
          }} />
        </div>
        <span style={{ minWidth: 110, lineHeight: 1.3 }}>{highLabel}</span>
        <span style={{
          minWidth: 28,
          textAlign: 'right',
          fontFamily: 'var(--mono)',
          color: 'var(--text2)',
        }}>
          {Math.round(clamped)}
        </span>
      </div>
    </div>
  )
}
